import { z } from 'zod';

// User role enum
export const UserRole = z.enum(['admin', 'accountant', 'client', 'preparer']);

// User schema
export const userSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  role: UserRole,
  phoneNumber: z.string().optional(),
  company: z.string().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Login schema
export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  rememberMe: z.boolean().optional(),
});

// Register schema
export const registerSchema = z
  .object({
    email: z.string().email(),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
    confirmPassword: z.string(),
    firstName: z.string().min(1).max(50),
    lastName: z.string().min(1).max(50),
    company: z.string().max(100).optional(),
    acceptTerms: z.literal(true).refine((val) => val === true, {
      message: 'You must accept the terms and conditions',
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

// Map numeric role values to string enum values (for backward compatibility with numeric URLs)
const ROLE_NUMBER_TO_STRING: Record<string, string> = {
  '0': 'client',
  '1': 'preparer',
  '2': 'admin',
};

// Valid role string values
const VALID_ROLES = ['client', 'preparer', 'admin'];

// User search filter schema for URL params (user admin page)
// Note: Uses preprocess instead of transform so input/output types match for TanStack Router navigation
export const userFilterSchema = z.object({
  search: z.string().optional(),
  // Accept both string enum values ('client', 'preparer', 'admin') and numeric values (0, 1, 2)
  // Always output string enum values for API compatibility
  role: z.preprocess(
    (val) => {
      if (val === undefined || val === null || val === '') return undefined;
      const strVal = String(val).toLowerCase();
      // If it's a valid role string, pass through
      if (VALID_ROLES.includes(strVal)) {
        return strVal;
      }
      // If it's a numeric string, convert to role string
      if (strVal in ROLE_NUMBER_TO_STRING) {
        return ROLE_NUMBER_TO_STRING[strVal];
      }
      // Invalid value - return undefined
      return undefined;
    },
    z.enum(['client', 'preparer', 'admin']).optional()
  ),
  // isActive accepts 'true'/'false'/'all' strings or boolean values (for URL serialization)
  // Note: Uses preprocess instead of transform so input/output types match for TanStack Router navigation
  // 'all' explicitly represents "no status filter" so the route default(true) doesn't override it
  isActive: z.preprocess(
    (val) => {
      if (val === 'all') return 'all';
      if (val === undefined || val === null) return undefined;
      if (val === true || val === 'true') return true;
      if (val === false || val === 'false') return false;
      return undefined;
    },
    z.union([z.boolean(), z.literal('all')]).optional()
  ),
  createdAfter: z.string().optional(),
  createdBefore: z.string().optional(),
  lastLoginAfter: z.string().optional(),
  lastLoginBefore: z.string().optional(),
});

// User pagination schema for URL params
export const userPaginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  rowsPerPage: z.coerce.number().int().positive().default(25),
  sortBy: z.string().default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

// Combined search params schema for the users route
export const usersSearchSchema = userFilterSchema.merge(userPaginationSchema);

// User form schema for editing
export const userFormSchema = z.object({
  email: z.string().email('Invalid email address').max(255),
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  phoneNumber: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        // Phone number validation - must be valid US format
        const cleaned = val.replace(/\D/g, '');
        if (cleaned.length === 10) return true;
        if (cleaned.length === 11 && cleaned.startsWith('1')) return true;
        return false;
      },
      { message: 'Invalid phone number format. Only US phone numbers are supported.' }
    ),
  company: z.string().max(200).optional().or(z.literal('')),
});

// Type exports
export type User = z.infer<typeof userSchema>;
export type UserRole = z.infer<typeof UserRole>;
export type LoginForm = z.infer<typeof loginSchema>;
export type RegisterForm = z.infer<typeof registerSchema>;
export type UserFilter = z.infer<typeof userFilterSchema>;
export type UserPagination = z.infer<typeof userPaginationSchema>;
export type UsersSearchParams = z.infer<typeof usersSearchSchema>;
export type UserForm = z.infer<typeof userFormSchema>;
